import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  MoreVertical,
  Mail,
  UserCheck,
  UserX,
  RefreshCw,
  Clock,
  Shield,
  Trash2,
  X,
  Loader2,
} from 'lucide-react';
import {
  teamMembersService,
  TeamMember,
  PendingInvitation,
  TeamMemberRole,
  TeamMemberStatus,
  InviteTeamMemberDto,
} from '../../services/team-members.service';
import { cn } from '../../lib/utils';
import { UserAvatar } from '../UserAvatar';
import { formatDistanceToNow } from 'date-fns';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '@centraoneplus/ui';
import {
  CARD,
  CARD_CLASS,
  CardFoot,
  ColumnHeader,
  EmptyState,
  FOCUS_RING,
  IconButton,
  Input,
  Notice,
  POP_CLASS,
  SearchInput,
  Segmented,
  StatusChip,
  Button as KitButton,
  type Tone,
} from '@shared/ui/kit';
import {
  Backdrop,
  Dialog,
  DialogFoot,
  DialogHead,
  FieldLabel,
  FILTER_SELECT,
  MENU_ITEM_DANGER,
  MENU_ITEM_OK,
  SectionHead,
  SettingsCard,
} from './chrome';
import { ThemedSelect } from '@components/common/ThemedSelect';

// A member's status, as a tone.
//
// Role is deliberately absent: it is not a state, and giving three roles three
// hues spends the screen's only chroma on a fact that never needs attention.
// Every role renders on the neutral chip.
const STATUS_TONE: Record<TeamMemberStatus, Tone> = {
  active: 'ok',
  inactive: 'neutral',
  pending: 'warn',
};

export const TeamMembersSettings: React.FC = () => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<TeamMemberRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<TeamMemberStatus | ''>('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'members' | 'invitations'>('members');
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  // Member pending delete-confirmation (drives the in-app confirm modal).
  const [deleteTarget, setDeleteTarget] = useState<TeamMember | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Active-ticket pre-check for the delete modal: null = not yet known / still
  // checking; a number = the target's active (open) ticket count. When > 0 the
  // modal warns and blocks deletion (mirrors the backend guard).
  const [deleteTargetTickets, setDeleteTargetTickets] = useState<number | null>(null);
  const [checkingTickets, setCheckingTickets] = useState(false);
  // Warning popup shown when a deactivate is blocked by active tickets (used
  // instead of a toast, so the restriction reads the same as the delete flow).
  const [deactivateWarning, setDeactivateWarning] = useState<{
    member: TeamMember;
    count: number;
  } | null>(null);

  // Only platform Super Admins (managing an org via "Manage Organization")
  // may delete a user from the system. The backend enforces this too.
  const isSuperAdmin = useAuthStore(
    (state) => state.user?.platformRole === 'SUPER_ADMIN',
  );
  const toast = useToast();

  // Invite form state
  const [inviteForm, setInviteForm] = useState<InviteTeamMemberDto>({
    email: '',
    role: 'agent',
    firstName: '',
    lastName: '',
  });
  const [inviting, setInviting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [membersData, invitationsData] = await Promise.all([
        teamMembersService.getTeamMembers({
          search: searchTerm || undefined,
          role: roleFilter || undefined,
          status: statusFilter || undefined,
        }),
        teamMembersService.getPendingInvitations(),
      ]);
      setMembers(membersData.data);
      setInvitations(invitationsData);
    } catch (err) {
      setError('Failed to load team members');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, roleFilter, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Close the invite modal and always reset the form, so reopening it never
  // shows stale values from a cancelled/failed attempt.
  const closeInviteModal = useCallback(() => {
    setShowInviteModal(false);
    setInviteForm({ email: '', role: 'agent', firstName: '', lastName: '' });
  }, []);

  const handleInvite = async () => {
    if (!inviteForm.email) return;

    setInviting(true);
    try {
      const invitedEmail = inviteForm.email;
      await teamMembersService.inviteTeamMember(inviteForm);
      closeInviteModal();
      toast.success('Invitation sent', invitedEmail);
      loadData();
    } catch (err: any) {
      // Close the modal (and clear the form) first, then surface the error on
      // the page banner — never inside the (now-closed) modal.
      closeInviteModal();
      // Show the server's actual reason (e.g. duplicate email, validation)
      // instead of a generic string, so the message is clear and actionable.
      const status = err?.response?.status;
      const rawMsg = err?.response?.data?.message;
      const serverMsg = Array.isArray(rawMsg)
        ? rawMsg.join(', ')
        : rawMsg || err?.message || 'Failed to send invitation';
      setError(status ? `(${status}) ${serverMsg}` : serverMsg);
      toast.error('Failed to send invitation', serverMsg);
      console.error(err);
    } finally {
      setInviting(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    setActionMenuOpen(null);
    // Pre-check active tickets: if the member still owns any, block with a
    // warning popup instead of attempting the call and surfacing the 409 as a
    // toast. The normal (no active tickets) deactivate flow is unchanged.
    try {
      const count = await teamMembersService.getActiveTicketCount(id);
      const member = members.find((m) => m.id === id);
      if (count > 0 && member) {
        setDeactivateWarning({ member, count });
        return;
      }
    } catch (err) {
      // Pre-check failed — fall through and let the backend guard be the
      // authoritative stop (surfaced as the toast below).
      console.error('Active-ticket pre-check failed:', err);
    }
    try {
      await teamMembersService.deactivateTeamMember(id);
      toast.success('Member deactivated');
      loadData();
    } catch (err: any) {
      setError('Failed to deactivate member');
      toast.error('Failed to deactivate member', err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.');
      console.error(err);
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await teamMembersService.reactivateTeamMember(id);
      toast.success('Member reactivated');
      loadData();
    } catch (err: any) {
      setError('Failed to reactivate member');
      toast.error('Failed to reactivate member', err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.');
      console.error(err);
    }
    setActionMenuOpen(null);
  };

  // Open the in-app confirmation popup for a member (replaces window.confirm).
  // Pre-checks the member's active tickets so the modal can warn and block the
  // delete before it's attempted, instead of only surfacing the backend 409.
  const openDeleteModal = async (member: TeamMember) => {
    setActionMenuOpen(null);
    setDeleteTargetTickets(null);
    setDeleteTarget(member);
    setCheckingTickets(true);
    try {
      const count = await teamMembersService.getActiveTicketCount(member.id);
      setDeleteTargetTickets(count);
    } catch (err) {
      // If the pre-check fails, don't block the UI — leave the count unknown
      // (0) and let the backend guard be the authoritative stop on submit.
      setDeleteTargetTickets(0);
      console.error('Active-ticket pre-check failed:', err);
    } finally {
      setCheckingTickets(false);
    }
  };

  // Close the delete modal and reset its transient pre-check state.
  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteTargetTickets(null);
    setCheckingTickets(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    // Guard: never submit a delete for a member with active tickets — the modal
    // hides the button in that case, this is belt-and-suspenders.
    if (deleteTargetTickets && deleteTargetTickets > 0) return;
    const name = getMemberDisplayName(deleteTarget);
    setDeleting(true);
    try {
      await teamMembersService.deleteTeamMember(deleteTarget.id);
      closeDeleteModal();
      // Explicit success feedback — previously a successful delete showed no
      // confirmation, so it looked like "no response".
      toast.success('User deleted', `${name} has been removed from the system.`);
      loadData();
    } catch (err: any) {
      closeDeleteModal();
      // Surface the real server status + message instead of a generic banner,
      // so a 403 (not a super admin), 404 (route/user missing), or 500 is
      // immediately visible rather than failing silently.
      const status = err?.response?.status;
      const serverMsg =
        err?.response?.data?.message || err?.message || 'Unknown error';
      const detail = status ? `(${status}) ${serverMsg}` : serverMsg;
      toast.error('Delete failed', `Could not delete ${name}: ${detail}`);
      console.error('Delete team member failed:', err?.response?.status, err?.response?.data || err);
    } finally {
      setDeleting(false);
    }
  };

  const handleResendInvitation = async (tokenId: string) => {
    try {
      await teamMembersService.resendInvitation(tokenId);
      toast.success('Invitation resent');
      loadData();
    } catch (err: any) {
      setError('Failed to resend invitation');
      toast.error('Failed to resend invitation', err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.');
      console.error(err);
    }
    setActionMenuOpen(null);
  };

  const handleCancelInvitation = async (tokenId: string) => {
    try {
      await teamMembersService.cancelInvitation(tokenId);
      toast.success('Invitation cancelled');
      loadData();
    } catch (err: any) {
      setError('Failed to cancel invitation');
      toast.error('Failed to cancel invitation', err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.');
      console.error(err);
    }
    setActionMenuOpen(null);
  };

  const getMemberDisplayName = (member: TeamMember): string => {
    if (member.displayName) return member.displayName;
    if (member.firstName || member.lastName) {
      return `${member.firstName || ''} ${member.lastName || ''}`.trim();
    }
    return member.email;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-[var(--co-brass-ink)]" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[14px]">
      {/* Head */}
      <SettingsCard>
        <SectionHead
          title="Team & roles"
          description="Who can sign in to this organization, and what each of them may do. Roles and teams do not route work — that is Automations."
          actions={
            <KitButton variant="primary" onClick={() => setShowInviteModal(true)}>
              <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
              Invite member
            </KitButton>
          }
        />
      </SettingsCard>

      {/* Error display — role="alert" (assertive live region) so screen
          readers announce the message as soon as it appears. */}
      {error && (
        <div role="alert" aria-live="assertive" className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <Notice tone="danger" headline="Something went wrong" body={error} />
          </div>
          <IconButton label="Dismiss error" onClick={() => setError(null)}>
            <X className="h-[15px] w-[15px]" aria-hidden="true" />
          </IconButton>
        </div>
      )}

      {/* Members and invitations are the same register of people, so they are
          two views of one card rather than two pages. */}
      <div className={`flex min-h-0 flex-col overflow-hidden ${CARD_CLASS}`} style={CARD}>
        <div className="flex flex-none flex-wrap items-center gap-3 border-b border-[var(--co-line-subtle)] px-5 py-[14px]">
          <Segmented
            label="Team register view"
            active={activeTab}
            onSelect={(id) => setActiveTab(id)}
            options={[
              { id: 'members' as const, label: `Members (${members.length})` },
              { id: 'invitations' as const, label: `Invitations (${invitations.length})` },
            ]}
          />

          {activeTab === 'members' && (
            <>
              <div className="min-w-[140px] flex-1">
                <SearchInput
                  placeholder="Search by name or email…"
                  aria-label="Search team members by name or email"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {/* One wrap unit, so a filter never drops to a row of its own. */}
              <div className="flex flex-none items-center gap-3">
              <ThemedSelect
                aria-label="Filter by role"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as TeamMemberRole | '')}
                className={FILTER_SELECT}
              >
                <option value="">All roles</option>
                <option value="admin">Admin</option>
                <option value="supervisor">Supervisor</option>
                <option value="agent">Agent</option>
              </ThemedSelect>
              <ThemedSelect
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as TeamMemberStatus | '')}
                className={FILTER_SELECT}
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </ThemedSelect>
              </div>
            </>
          )}
        </div>

        {activeTab === 'members' && (
          <>
            <ColumnHeader
              columns={[
                { id: 'member', label: 'Member' },
                { id: 'role', label: 'Role', width: 110 },
                { id: 'status', label: 'Status', width: 116 },
                { id: 'lastActive', label: 'Last active', width: 108 },
                { id: 'actions', label: '', width: 44 },
              ]}
            />

            {/* `overflow-visible` so the row 3-dot dropdown can render past the
                card's rounded edges. */}
            <div className="overflow-visible">
              {members.map((member, index) => (
                <div
                  key={member.id}
                  role="row"
                  className="flex items-center gap-3 border-b border-l-2 border-[var(--co-line-subtle)] px-[14px] py-[11px] last:border-b-0"
                  style={{
                    borderLeftColor: 'transparent',
                    background: member.status === 'inactive' ? 'var(--co-sunken)' : undefined,
                  }}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-[11px]">
                    <UserAvatar
                      src={member.avatarUrl}
                      name={getMemberDisplayName(member)}
                      className="h-7 w-7 shrink-0 rounded-[7px] bg-[var(--co-brass-wash)] text-[11px] font-semibold text-[var(--co-brass-ink)]"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-[13.5px] font-semibold text-[var(--co-ink)]">
                        {getMemberDisplayName(member)}
                      </div>
                      <div className="co-mono mt-[3px] truncate text-[10.5px] text-[var(--co-ink-3)]">
                        {member.email}
                      </div>
                    </div>
                  </div>

                  <div className="flex-none" style={{ width: 110 }}>
                    {/* Neutral for every role. A role is not a state: colouring
                        three of them spends the only chroma on the screen on a
                        fact that never needs attention. */}
                    <StatusChip tone="neutral">{member.role}</StatusChip>
                  </div>

                  <div className="flex flex-none items-center gap-2" style={{ width: 116 }}>
                    <StatusChip tone={STATUS_TONE[member.status]}>{member.status}</StatusChip>
                    {member.mfaEnabled && (
                      <span title="MFA enabled" aria-label="MFA enabled">
                        <Shield
                          className="h-[14px] w-[14px]"
                          style={{ color: 'var(--co-green-ink)' }}
                          aria-hidden="true"
                        />
                      </span>
                    )}
                  </div>

                  <div
                    className="flex-none text-[12.5px] text-[var(--co-ink-3)]"
                    style={{ width: 108 }}
                  >
                    {member.lastLoginAt
                      ? formatDistanceToNow(new Date(member.lastLoginAt), { addSuffix: true })
                      : 'Never'}
                  </div>

                  <div className="relative flex-none text-right" style={{ width: 44 }}>
                    <button
                      type="button"
                      aria-label={`Actions for ${getMemberDisplayName(member)}`}
                      aria-haspopup="menu"
                      aria-expanded={actionMenuOpen === member.id}
                      onClick={() => setActionMenuOpen(actionMenuOpen === member.id ? null : member.id)}
                      className={`rounded-md p-[6px] text-[var(--co-ink-3)] transition-colors hover:bg-[var(--co-selected)] hover:text-[var(--co-ink)] ${FOCUS_RING}`}
                    >
                      <MoreVertical className="h-4 w-4" aria-hidden="true" />
                    </button>
                    {actionMenuOpen === member.id && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setActionMenuOpen(null)}
                        />
                        <div
                          role="menu"
                          className={cn(
                            'absolute right-0 z-50 w-48 overflow-hidden rounded-lg',
                            POP_CLASS,
                            // Flip upward for the last row so the menu isn't clipped by the
                            // wrapper's overflow-hidden (needed to preserve the rounded corners).
                            index === members.length - 1 && members.length > 1
                              ? 'bottom-full mb-2'
                              : 'mt-2',
                          )}
                          style={{ boxShadow: 'var(--co-shadow-pop)' }}
                        >
                          {member.status === 'active' ? (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => handleDeactivate(member.id)}
                              className={MENU_ITEM_DANGER}
                            >
                              <UserX className="h-4 w-4" aria-hidden="true" />
                              Deactivate
                            </button>
                          ) : (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => handleReactivate(member.id)}
                              className={MENU_ITEM_OK}
                            >
                              <UserCheck className="h-4 w-4" aria-hidden="true" />
                              Reactivate
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => openDeleteModal(member)}
                              className={`${MENU_ITEM_DANGER} border-t border-[var(--co-line-subtle)]`}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                              Delete
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}

              {members.length === 0 && (
                <EmptyState
                  title="No team members match this view"
                  body={
                    searchTerm || roleFilter || statusFilter
                      ? 'Every member is filtered out by the search or the role/status filters above. Clear them to see the whole roster.'
                      : 'Nobody has been invited to this organization yet.'
                  }
                />
              )}
            </div>

            <CardFoot
              left={`${members.length} member${members.length === 1 ? '' : 's'} shown`}
              note="Deactivated members stay on this list until they are removed."
            />
          </>
        )}

        {activeTab === 'invitations' && (
          <>
            <ColumnHeader
              columns={[
                { id: 'email', label: 'Email' },
                { id: 'role', label: 'Role', width: 130 },
                { id: 'expires', label: 'Expires', width: 170 },
                { id: 'actions', label: '', width: 96 },
              ]}
            />

            <div>
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  role="row"
                  className="flex items-center gap-3 border-b border-l-2 border-[var(--co-line-subtle)] px-[14px] py-[11px] last:border-b-0"
                  style={{ borderLeftColor: 'transparent' }}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-[11px]">
                    <Mail
                      className="h-[15px] w-[15px] flex-none text-[var(--co-ink-4)]"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-[13.5px] font-semibold text-[var(--co-ink)]">
                        {invitation.email}
                      </div>
                      {(invitation.firstName || invitation.lastName) && (
                        <div className="mt-[3px] truncate text-[12px] text-[var(--co-ink-3)]">
                          {invitation.firstName} {invitation.lastName}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex-none" style={{ width: 130 }}>
                    <StatusChip tone="neutral">{invitation.role}</StatusChip>
                  </div>

                  <div
                    className="flex flex-none items-center gap-[6px] text-[12.5px] text-[var(--co-ink-3)]"
                    style={{ width: 170 }}
                  >
                    <Clock className="h-[13px] w-[13px]" aria-hidden="true" />
                    {formatDistanceToNow(new Date(invitation.expiresAt), { addSuffix: true })}
                  </div>

                  <div className="flex flex-none justify-end gap-2" style={{ width: 96 }}>
                    <IconButton
                      label={`Resend invitation to ${invitation.email}`}
                      onClick={() => handleResendInvitation(invitation.id)}
                    >
                      <RefreshCw className="h-[15px] w-[15px]" aria-hidden="true" />
                    </IconButton>
                    <IconButton
                      label={`Cancel invitation to ${invitation.email}`}
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="text-[var(--co-burgundy)] hover:border-[var(--co-burgundy)]"
                    >
                      <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
                    </IconButton>
                  </div>
                </div>
              ))}

              {invitations.length === 0 && (
                <EmptyState
                  title="No invitations outstanding"
                  body="Everyone invited has either accepted or had their invitation cancelled."
                />
              )}
            </div>

            <CardFoot
              left={`${invitations.length} invitation${invitations.length === 1 ? '' : 's'} shown`}
              note="An invitation expires on its own; resending issues a fresh one."
            />
          </>
        )}
      </div>

      {/* Invite Modal — rendered into document.body so the fixed backdrop
          covers the full viewport even when an ancestor has a transform/filter
          (which would otherwise make `fixed` position relative to it) */}
      {showInviteModal && createPortal(
        <Backdrop>
          <Dialog labelledBy="invite-member-title" onClose={closeInviteModal}>
            <DialogHead
              id="invite-member-title"
              title="Invite team member"
              onClose={closeInviteModal}
            />
            <div className="space-y-4 px-6 py-5">
              <div>
                <FieldLabel htmlFor="invite-email">Email address *</FieldLabel>
                <div className="mt-[7px]">
                  <Input
                    id="invite-email"
                    name="email"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="colleague@company.com"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel htmlFor="invite-first-name">First name</FieldLabel>
                  <div className="mt-[7px]">
                    <Input
                      id="invite-first-name"
                      name="firstName"
                      type="text"
                      value={inviteForm.firstName}
                      onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                      placeholder="John"
                    />
                  </div>
                </div>
                <div>
                  <FieldLabel htmlFor="invite-last-name">Last name</FieldLabel>
                  <div className="mt-[7px]">
                    <Input
                      id="invite-last-name"
                      name="lastName"
                      type="text"
                      value={inviteForm.lastName}
                      onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>
              <div>
                <FieldLabel htmlFor="invite-role">Role *</FieldLabel>
                <div className="mt-[7px]">
                  <ThemedSelect
                    id="invite-role"
                    name="role"
                    value={inviteForm.role}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, role: e.target.value as TeamMemberRole })
                    }
                    className={`${FILTER_SELECT} w-full`}
                  >
                    <option value="agent">Agent</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="admin">Admin</option>
                  </ThemedSelect>
                </div>
              </div>
            </div>
            <DialogFoot>
              <KitButton onClick={closeInviteModal}>Cancel</KitButton>
              <KitButton
                variant="primary"
                onClick={handleInvite}
                disabled={!inviteForm.email || inviting}
              >
                {inviting ? 'Sending…' : 'Send invitation'}
              </KitButton>
            </DialogFoot>
          </Dialog>
        </Backdrop>,
        document.body,
      )}

      {/* Delete confirmation popup — in-app replacement for window.confirm.
          Rendered into document.body so the fixed backdrop covers the full
          viewport regardless of any transformed ancestor. */}
      {deleteTarget && createPortal(
        <Backdrop>
          <Dialog labelledBy="delete-user-title" onClose={deleting ? undefined : closeDeleteModal}>
            <DialogHead id="delete-user-title" title="Delete user" tone="danger" />
            <div className="px-6 py-5">
              {checkingTickets ? (
                <p className="flex items-center gap-2 text-[12.5px] text-[var(--co-ink-2)]">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Checking assigned tickets…
                </p>
              ) : deleteTargetTickets && deleteTargetTickets > 0 ? (
                // Restricted: the member still owns active tickets. Warn and
                // block deletion until those tickets are reassigned/resolved.
                <Notice
                  tone="warn"
                  headline="This user can’t be deleted yet"
                  body={
                    <>
                      <strong className="font-semibold text-[var(--co-ink)]">
                        {getMemberDisplayName(deleteTarget)}
                      </strong>{' '}
                      is assigned to{' '}
                      <strong className="font-semibold">
                        {deleteTargetTickets} active ticket
                        {deleteTargetTickets === 1 ? '' : 's'}
                      </strong>
                      . Reassign or resolve those tickets first, then try again.
                    </>
                  }
                />
              ) : (
                <p className="text-[12.5px] leading-[1.55] text-[var(--co-ink-2)]">
                  Delete{' '}
                  <strong className="font-semibold text-[var(--co-ink)]">
                    {getMemberDisplayName(deleteTarget)}
                  </strong>{' '}
                  from the system? This permanently removes the user and cannot be undone.
                </p>
              )}
            </div>
            <DialogFoot>
              <KitButton onClick={closeDeleteModal} disabled={deleting}>
                {deleteTargetTickets && deleteTargetTickets > 0 ? 'Close' : 'Cancel'}
              </KitButton>
              {/* Delete is offered only once the pre-check confirms no active
                  tickets — otherwise it's hidden (the member is restricted). */}
              {!checkingTickets && !(deleteTargetTickets && deleteTargetTickets > 0) && (
                <KitButton variant="destructive" onClick={confirmDelete} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </KitButton>
              )}
            </DialogFoot>
          </Dialog>
        </Backdrop>,
        document.body,
      )}

      {/* Deactivate blocked — warning popup (shown instead of a toast when the
          member still owns active tickets). Informational only: no action
          button, since the deactivate is restricted until the tickets move. */}
      {deactivateWarning && createPortal(
        <Backdrop>
          <Dialog labelledBy="deactivate-warning-title" onClose={() => setDeactivateWarning(null)}>
            <DialogHead id="deactivate-warning-title" title="Can’t deactivate user" tone="warn" />
            <div className="px-6 py-5">
              <p className="text-[12.5px] leading-[1.55] text-[var(--co-ink-2)]">
                <strong className="font-semibold text-[var(--co-ink)]">
                  {getMemberDisplayName(deactivateWarning.member)}
                </strong>{' '}
                is assigned to{' '}
                <strong className="font-semibold">
                  {deactivateWarning.count} active ticket
                  {deactivateWarning.count === 1 ? '' : 's'}
                </strong>
                . Reassign or resolve those tickets first, then try again.
              </p>
            </div>
            <DialogFoot>
              <KitButton onClick={() => setDeactivateWarning(null)}>Close</KitButton>
            </DialogFoot>
          </Dialog>
        </Backdrop>,
        document.body,
      )}
    </div>
  );
};


export default TeamMembersSettings;
